import { FC, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Navigate, useSearchParams } from "react-router";

import { ButtonLink } from "@/components/Interface/Button/Button";
import { CardContainer } from "@/components/Layout/BaseLayouts/CardContainer";
import { LoadingSpinner } from "@/components/Layout/LoadingSpinner/LoadingSpinner";
import { KYCWarning } from "@/components/Portal/Cockpit/KYCWarning/KYCWarning";
import { useIdentificationStatus } from "@/hooks/identificationStatusQueryOptions";
import { useNeedsIDCheck } from "@/hooks/useNeedsIDCheck";
import { portalRoutes } from "@/router/routes/portalRoutes";
import { IdentificationStatusResponseKycStatus } from "@/services/model";
import { isIdCheckPageActionable } from "@/utils/kycTodoConfig";

/**
 * Dedicated ID-check page: full KYC content and CTA that used to live inline
 * in the cockpit To-dos section.
 */
export const IdCheckPage: FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const fromOrder = searchParams.get("from") === "order";
  const needsIdCheck = useNeedsIDCheck();
  const staleTime = useRef(0);
  const { data: identificationStatus } = useIdentificationStatus(staleTime);

  if (!identificationStatus) {
    return (
      <section data-testid="id-check-page" className="w-full pb-8">
        <CardContainer
          className="flex w-full flex-col gap-6 rounded-lg p-6"
          variant="ivory"
        >
          <LoadingSpinner />
        </CardContainer>
      </section>
    );
  }

  if (
    identificationStatus.kycStatus ===
    IdentificationStatusResponseKycStatus.KYC_data_mismatch
  ) {
    return <Navigate to={portalRoutes.user.personal} replace />;
  }

  const shouldRedirectAway =
    needsIdCheck === false ||
    !isIdCheckPageActionable(identificationStatus.kycStatus);

  if (shouldRedirectAway) {
    return <Navigate to={portalRoutes.cockpit.base} replace />;
  }

  return (
    <section data-testid="id-check-page" className="w-full pb-8">
      <CardContainer
        className="flex w-full flex-col gap-6 rounded-lg p-6"
        variant="ivory"
      >
        {/* Text only, no chevron - matches the design's leave-for-later control. */}
        <ButtonLink
          data-testid="id-check-back"
          className="self-start"
          variant="tertiary"
          to={portalRoutes.cockpit.base}
        >
          {t("portal:common.buttons.skipForLater")}
        </ButtonLink>

        <KYCWarning
          fromOrder={
            fromOrder &&
            identificationStatus.kycStatus ===
              IdentificationStatusResponseKycStatus.OPEN
          }
        />
      </CardContainer>
    </section>
  );
};
